import { z } from "zod";

import { frozenText } from "../criticalContent";
import { pageRefSchema, reviewStateSchema } from "../schema";

const sceneIdSchema = z.enum(["M2S1", "M2S2", "M2S3"]);
const taskIdSchema = z.enum(["T05", "T06", "T07"]);
const nodeIdSchema = z.string().regex(/^(M2S[1-3]_N0[1-9]|M2S2_GX0[78])$/);
const claimIdSchema = z.string().regex(/^M2S[1-3]_CLAIM_[A-Z0-9_]+$/);

const textbookSourceSchema = z.object({
  sourceCategory: z.literal("教材正文"),
  provenanceType: z.literal("textbook"),
  sourceRefs: z.tuple([z.literal("SRC_TEXTBOOK_CH3")]),
  citationLabel: z.literal("《中国共产党简史》第三章"),
  pageRefs: z.array(pageRefSchema).min(1),
});

const localExtensionSourceSchema = z.object({
  sourceCategory: z.literal("地方史料拓展"),
  provenanceType: z.literal("local_extension"),
  sourceRefs: z.array(z.string().regex(/^SRC_[A-Z0-9_]+$/)).min(1),
  citationLabel: z.string().min(1),
  pageRefs: z.tuple([]),
});

const nodeSourceSchema = z.discriminatedUnion("sourceCategory", [
  textbookSourceSchema,
  localExtensionSourceSchema,
]);

const assetSuggestionSchema = z.object({
  key: z.string().regex(/^M2S[1-3]_ASSET_[A-Z0-9_]+$/),
  kind: z.enum(["programmatic_3d", "programmatic_2_5d", "archive_media", "interface"]),
  title: z.string().min(1),
  usage: z.string().min(1),
  provenanceGuard: z.string().min(1),
  suggestedVisibleLabel: z.string().min(1).optional(),
});

const presentationTypeSchema = z.enum([
  "strategic_situation_map",
  "battle_map",
  "evidence_wall",
  "solemn_archive_witness",
  "tactical_transition",
  "governance_timeline",
  "terrain_expansion_map",
  "dual_theater_map",
  "people_support_witness",
  "organization_network",
  "local_extension_timeline",
  "principle_balance",
  "document_comparison",
  "conference_document_wall",
  "strategy_board",
]);

export const m2BlueprintNodeSchema = z
  .object({
    id: nodeIdSchema,
    sceneId: sceneIdSchema,
    order: z.number().int().positive(),
    nodeType: z.enum(["textbook_claim", "solemn_archive_witness", "local_extension"]),
    title: z.string().min(1),
    timeAnchor: z.string().min(1),
    estimatedSeconds: z.number().int().positive(),
    claimId: claimIdSchema,
    claim: z.string().min(1),
    bodyZh: z.string().min(1),
    presentationType: presentationTypeSchema,
    assetSuggestions: z.array(assetSuggestionSchema).min(1),
    source: nodeSourceSchema,
    reviewState: reviewStateSchema,
    visibleLabel: z.string().min(1).optional(),
  })
  .superRefine((node, context) => {
    const isLocal = node.nodeType === "local_extension";
    if (isLocal !== (node.source.sourceCategory === "地方史料拓展")) {
      context.addIssue({
        code: "custom",
        message: `${node.id}的节点类型与来源类别不一致。`,
        path: ["source"],
      });
    }
    if (isLocal && node.visibleLabel !== "拓展材料") {
      context.addIssue({
        code: "custom",
        message: `${node.id}必须以“拓展材料”作为可见标签。`,
        path: ["visibleLabel"],
      });
    }
    if (!isLocal && node.source.pageRefs.length === 0) {
      context.addIssue({
        code: "custom",
        message: `${node.id}的教材主张必须登记书内印刷页和PDF页。`,
        path: ["source", "pageRefs"],
      });
    }
  });

const extensionLabelSchema = z.object({
  id: z.literal("GX05_LABEL"),
  label: z.literal("【广西融入⑤】"),
  visibleLabel: z.literal("拓展材料"),
  appliesTo: z.tuple([z.literal("M2S2_GX08"), z.literal("M2S2_GX07")]),
});

const narrationTimingSchema = z
  .object({
    mainNarrationSeconds: z.number().int().positive(),
    extensionNarrationSeconds: z.number().int().nonnegative(),
    totalNarrationSeconds: z.number().int().positive(),
  })
  .superRefine((timing, context) => {
    if (
      timing.totalNarrationSeconds !==
      timing.mainNarrationSeconds + timing.extensionNarrationSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: "讲授总时长必须等于主体讲授与地方拓展之和。",
        path: ["totalNarrationSeconds"],
      });
    }
  });

const expectedMainNarrationSeconds = {
  M2S1: 240,
  M2S2: 240,
  M2S3: 180,
} as const;

export const m2SceneBlueprintSchema = z
  .object({
    id: sceneIdSchema,
    order: z.number().int().min(1).max(3),
    title: z.string().min(1),
    identity: z.literal("抗战史料研学员"),
    timeLabel: z.string().min(1),
    mainVenue: z.string().min(1),
    archiveSlices: z.array(z.string().min(1)),
    taskIds: z.array(taskIdSchema).length(1),
    sequence: z.array(z.string().min(1)).min(2),
    nodes: z.array(m2BlueprintNodeSchema).min(1),
    extensionLabel: extensionLabelSchema.optional(),
    timing: narrationTimingSchema,
    reviewState: reviewStateSchema,
  })
  .superRefine((scene, context) => {
    const mainSeconds = scene.nodes
      .filter((node) => node.source.provenanceType === "textbook")
      .reduce((total, node) => total + node.estimatedSeconds, 0);
    const extensionSeconds = scene.nodes
      .filter((node) => node.source.provenanceType === "local_extension")
      .reduce((total, node) => total + node.estimatedSeconds, 0);

    if (scene.timing.mainNarrationSeconds !== mainSeconds) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}主体讲授时长必须等于教材节点时长之和。`,
        path: ["timing", "mainNarrationSeconds"],
      });
    }
    if (scene.timing.extensionNarrationSeconds !== extensionSeconds) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}拓展讲授时长必须等于地方史料节点时长之和。`,
        path: ["timing", "extensionNarrationSeconds"],
      });
    }
    if (mainSeconds !== expectedMainNarrationSeconds[scene.id]) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}主体讲授必须保持原方案约${expectedMainNarrationSeconds[scene.id] / 60}分钟。`,
        path: ["timing", "mainNarrationSeconds"],
      });
    }
    if (scene.id === "M2S2" && (extensionSeconds < 198 || extensionSeconds > 246)) {
      context.addIssue({
        code: "custom",
        message: "M2S2广西融入⑤全文讲授须保留约3.3—4.1分钟，不得以压缩文字换取时长。",
        path: ["timing", "extensionNarrationSeconds"],
      });
    }
    if (scene.id !== "M2S2" && extensionSeconds !== 0) {
      context.addIssue({
        code: "custom",
        message: `${scene.id}不得登记广西融入⑤时长。`,
        path: ["timing", "extensionNarrationSeconds"],
      });
    }
  });

const reviewNoteSchema = z.object({
  id: z.string().regex(/^M2_REVIEW_[A-Z0-9_]+$/),
  scope: z.string().min(1),
  priority: z.enum(["before_frontend_release", "before_formal_release"]),
  state: z.literal("open"),
  note: z.string().min(1),
});

const expectedSequences = {
  M2S1: ["M2S1_N01", "M2S1_N02", "M2S1_N03", "M2S1_N04", "M2S1_N05", "T05"],
  M2S2: [
    "M2S2_N01",
    "M2S2_N02",
    "M2S2_N03",
    "M2S2_N04",
    "M2S2_N05",
    "GX05_LABEL",
    "M2S2_GX08",
    "M2S2_GX07",
    "T06",
  ],
  M2S3: ["M2S3_N01", "M2S3_N02", "M2S3_N03", "M2S3_N04", "T07"],
} as const;

const expectedTaskMap = {
  M2S1: "T05",
  M2S2: "T06",
  M2S3: "T07",
} as const;

export const m2BlueprintSchema = z
  .object({
    id: z.literal("M2"),
    version: z.literal("1.0.0"),
    title: z.literal("开展敌后抗日游击战争和坚持统一战线中的独立自主"),
    identity: z.literal("抗战史料研学员"),
    pageRange: z.object({ print: z.literal("78—83"), pdf: z.literal("98—103") }),
    sceneIds: z.tuple([z.literal("M2S1"), z.literal("M2S2"), z.literal("M2S3")]),
    taskIds: z.tuple([z.literal("T05"), z.literal("T06"), z.literal("T07")]),
    scenes: z.array(m2SceneBlueprintSchema).length(3),
    timing: narrationTimingSchema,
    reviewState: reviewStateSchema,
    reviewNotes: z.array(reviewNoteSchema).min(1),
  })
  .superRefine((blueprint, context) => {
    if (blueprint.scenes.map((scene) => scene.id).join(",") !== blueprint.sceneIds.join(",")) {
      context.addIssue({
        code: "custom",
        message: "M2三个场景必须按M2S1、M2S2、M2S3声明。",
        path: ["scenes"],
      });
    }

    const allNodeIds = blueprint.scenes.flatMap((scene) => scene.nodes.map((node) => node.id));
    const allClaimIds = blueprint.scenes.flatMap((scene) =>
      scene.nodes.map((node) => node.claimId),
    );
    if (new Set(allNodeIds).size !== allNodeIds.length) {
      context.addIssue({ code: "custom", message: "M2节点ID必须唯一。", path: ["scenes"] });
    }
    if (new Set(allClaimIds).size !== allClaimIds.length) {
      context.addIssue({ code: "custom", message: "M2 claimId必须唯一。", path: ["scenes"] });
    }

    const mainSeconds = blueprint.scenes.reduce(
      (total, scene) => total + scene.timing.mainNarrationSeconds,
      0,
    );
    const extensionSeconds = blueprint.scenes.reduce(
      (total, scene) => total + scene.timing.extensionNarrationSeconds,
      0,
    );
    if (
      blueprint.timing.mainNarrationSeconds !== mainSeconds ||
      blueprint.timing.extensionNarrationSeconds !== extensionSeconds ||
      blueprint.timing.totalNarrationSeconds !== mainSeconds + extensionSeconds
    ) {
      context.addIssue({
        code: "custom",
        message: "M2模块讲授时长必须等于三个场景的时长汇总。",
        path: ["timing"],
      });
    }

    for (const [sceneIndex, scene] of blueprint.scenes.entries()) {
      const expectedSequence = expectedSequences[scene.id];
      if (scene.sequence.join("|") !== expectedSequence.join("|")) {
        context.addIssue({
          code: "custom",
          message: `${scene.id}节点与任务顺序与需求基线不一致。`,
          path: ["scenes", sceneIndex, "sequence"],
        });
      }
      if (scene.taskIds[0] !== expectedTaskMap[scene.id]) {
        context.addIssue({
          code: "custom",
          message: `${scene.id}的正式任务映射不正确。`,
          path: ["scenes", sceneIndex, "taskIds"],
        });
      }
      if (scene.nodes.some((node) => node.sceneId !== scene.id)) {
        context.addIssue({
          code: "custom",
          message: `${scene.id}存在归属错误的节点。`,
          path: ["scenes", sceneIndex, "nodes"],
        });
      }
      const orders = scene.nodes.map((node) => node.order);
      if (
        new Set(orders).size !== orders.length ||
        orders.some((order, index) => index > 0 && order <= (orders[index - 1] ?? 0))
      ) {
        context.addIssue({
          code: "custom",
          message: `${scene.id}节点order必须唯一且严格递增。`,
          path: ["scenes", sceneIndex, "nodes"],
        });
      }
    }

    const m2s2 = blueprint.scenes.find((scene) => scene.id === "M2S2");
    if (!m2s2?.extensionLabel) {
      context.addIssue({
        code: "custom",
        message: "M2S2必须声明且只声明一个广西融入⑤标签。",
        path: ["scenes"],
      });
    }
    if (blueprint.scenes.some((scene) => scene.id !== "M2S2" && scene.extensionLabel)) {
      context.addIssue({
        code: "custom",
        message: "广西融入⑤不得移入M2S1或M2S3。",
        path: ["scenes"],
      });
    }
  });

export type M2BlueprintNode = z.infer<typeof m2BlueprintNodeSchema>;
export type M2SceneBlueprint = z.infer<typeof m2SceneBlueprintSchema>;
export type M2Blueprint = z.infer<typeof m2BlueprintSchema>;

const textbookSource = (print: string, pdf: string): M2BlueprintNode["source"] => ({
  sourceCategory: "教材正文",
  provenanceType: "textbook",
  sourceRefs: ["SRC_TEXTBOOK_CH3"],
  citationLabel: "《中国共产党简史》第三章",
  pageRefs: [{ print, pdf }],
});

const localExtensionSource = (
  sourceRefs: string[],
  citationLabel: string,
): M2BlueprintNode["source"] => ({
  sourceCategory: "地方史料拓展",
  provenanceType: "local_extension",
  sourceRefs,
  citationLabel,
  pageRefs: [],
});

export const m2Blueprint = {
  id: "M2",
  version: "1.0.0",
  title: "开展敌后抗日游击战争和坚持统一战线中的独立自主",
  identity: "抗战史料研学员",
  pageRange: { print: "78—83", pdf: "98—103" },
  sceneIds: ["M2S1", "M2S2", "M2S3"],
  taskIds: ["T05", "T06", "T07"],
  scenes: [
    {
      id: "M2S1",
      order: 1,
      title: "八路军开赴抗日前线",
      identity: "抗战史料研学员",
      timeLabel: "主场景1937年9月25日；南京背景档案自12月13日起六周",
      mainVenue: "山西灵丘平型关一带",
      archiveSlices: ["南京庄重史料见证切片"],
      taskIds: ["T05"],
      sequence: ["M2S1_N01", "M2S1_N02", "M2S1_N03", "M2S1_N04", "M2S1_N05", "T05"],
      nodes: [
        {
          id: "M2S1_N01",
          sceneId: "M2S1",
          order: 10,
          nodeType: "textbook_claim",
          title: "战略防御阶段的两大特点",
          timeAnchor: "1937年全民族抗战战略防御阶段",
          estimatedSeconds: 40,
          claimId: "M2S1_CLAIM_DEFENSIVE_STAGE_TWO_FEATURES",
          claim:
            "战略防御阶段日军对正面战场的攻势达到顶点，中国共产党领导的人民军队则开展敌后游击战争并迅速壮大。",
          bodyZh:
            "红军改编为国民革命军后，迅速开赴抗日前线。此刻，抗日战争正处在战略防御阶段。这个阶段呈现出两大特点：一是日军分路深入中国广大领土，对中国正面战场的攻势达到顶点；二是中国共产党领导的人民军队开展敌后游击战争，并迅速壮大起来。",
          presentationType: "strategic_situation_map",
          assetSuggestions: [
            {
              key: "M2S1_ASSET_DEFENSIVE_STAGE_MAP",
              kind: "programmatic_2_5d",
              title: "战略防御阶段双层态势图",
              usage: "分层展示正面战场攻势与人民军队敌后展开，用对比而非同一现场叙事。",
              provenanceGuard: "程序化战略示意必须标注非档案地图，不得伪造精确兵力分布。",
              suggestedVisibleLabel: "战略态势示意·非档案地图",
            },
          ],
          source: textbookSource("78", "98"),
          reviewState: "pending_review",
        },
        {
          id: "M2S1_N02",
          sceneId: "M2S1",
          order: 20,
          nodeType: "textbook_claim",
          title: "1937年9月25日·平型关首战告捷",
          timeAnchor: "1937年9月25日",
          estimatedSeconds: 42,
          claimId: "M2S1_CLAIM_PINGXINGGUAN_BATTLE_FACTS",
          claim: "1937年9月25日，八路军第一一五师主力在平型关伏击日军，首战告捷。",
          bodyZh:
            "八路军到达山西抗日前线后，即取得平型关战斗重大胜利。1937年9月25日，八路军第一一五师主力在平型关伏击日军，首战告捷，一举歼灭日军1000余人，击毁日军汽车100余辆，缴获一批辎重和武器。",
          presentationType: "battle_map",
          assetSuggestions: [
            {
              key: "M2S1_ASSET_PINGXINGGUAN_TERRAIN",
              kind: "programmatic_3d",
              title: "平型关地形与伏击路径沙盘",
              usage: "以地形、时间和战斗结果三层信息解释战斗档案。",
              provenanceGuard: "线路和队列为教学示意，未经战史审核不宣称精确战术复原。",
              suggestedVisibleLabel: "场景化复原·非档案影像",
            },
          ],
          source: textbookSource("79", "99"),
          reviewState: "pending_review",
        },
        {
          id: "M2S1_N03",
          sceneId: "M2S1",
          order: 30,
          nodeType: "textbook_claim",
          title: "历史意义",
          timeAnchor: "1937年9月之后",
          estimatedSeconds: 54,
          claimId: "M2S1_CLAIM_PINGXINGGUAN_SIGNIFICANCE",
          claim: "平型关大捷是全民族抗战爆发后中国军队主动对日作战取得的第一个重大胜利。",
          bodyZh:
            "平型关大捷是全民族抗战爆发后中国军队主动对日作战取得的第一个重大胜利，打破了日军“不可战胜”的神话，极大地振奋了全国军民的抗战信心，提高了共产党和八路军的声望。朱德在家书中写道：此战使“全线上气为之一壮。如各军都同我们一样，那就不难打退敌人和消灭敌人”。",
          presentationType: "evidence_wall",
          assetSuggestions: [
            {
              key: "M2S1_ASSET_SIGNIFICANCE_EVIDENCE",
              kind: "interface",
              title: "战果-意义-朱德家书三层证据墙",
              usage: "先核对战斗事实，再引导学习者区分直接战果与历史意义。",
              provenanceGuard: "家书引文只按教材载录呈现，不伪造手稿原件。",
            },
          ],
          source: textbookSource("79—80", "99—100"),
          reviewState: "pending_review",
        },
        {
          id: "M2S1_N04",
          sceneId: "M2S1",
          order: 40,
          nodeType: "solemn_archive_witness",
          title: "南京大屠杀·庄重史料见证",
          timeAnchor: "1937年12月13日起六周",
          estimatedSeconds: 58,
          claimId: "M2S1_CLAIM_NANJING_MASSACRE_WITNESS",
          claim: "侵华日军在占领南京后制造了骇人听闻的大屠杀惨案，是骇人听闻的反人类罪行。",
          bodyZh:
            "1937年12月13日，日军占领南京后，进行了长达6周骇人听闻的血腥大屠杀，中国平民和解除武装的军人被枪杀、焚烧、活埋以及用其他方法残忍杀害者，达30万人以上。侵华日军一手制造的这一灭绝人性的大屠杀惨案，是骇人听闻的反人类罪行，是人类历史上十分黑暗的一页。",
          presentationType: "solemn_archive_witness",
          assetSuggestions: [
            {
              key: "M2S1_ASSET_NANJING_WITNESS",
              kind: "archive_media",
              title: "南京大屠杀庄重见证档案卡",
              usage: "仅用时间、教材原文、来源和非猎奇图像建立史料见证。",
              provenanceGuard:
                "正式素材须通过来源与权利审核；禁止游戏化积分、失败重来、庆祝动画和猎奇特写。",
            },
          ],
          source: textbookSource("79", "99"),
          reviewState: "pending_review",
        },
        {
          id: "M2S1_N05",
          sceneId: "M2S1",
          order: 50,
          nodeType: "textbook_claim",
          title: "从运动战向抗日游击战争的战略转变",
          timeAnchor: "人民军队深入敌人后方以后",
          estimatedSeconds: 46,
          claimId: "M2S1_CLAIM_GUERRILLA_STRATEGIC_SHIFT",
          claim: "党确定人民军队深入敌后后基本采取游击战，但不放松有利条件下的运动战。",
          bodyZh:
            "在深入敌人后方以后，八路军应该怎样作战，怎样打击敌人？党确定了基本的是游击战，但不放松有利条件下的运动战的作战方针。党领导的人民军队在军事战略上实行了重大转变，就是由土地革命战争后期的运动战向抗日游击战争转变。",
          presentationType: "tactical_transition",
          assetSuggestions: [
            {
              key: "M2S1_ASSET_TACTICAL_TRANSITION",
              kind: "interface",
              title: "作战方针转变对比板",
              usage: "用“基本作战形式”和“有利条件下的配合形式”两列防止简化为单一战法。",
              provenanceGuard: "不虚构学习者作为指挥员改变战局的分支。",
            },
          ],
          source: textbookSource("80", "100"),
          reviewState: "pending_review",
        },
      ],
      timing: {
        mainNarrationSeconds: 240,
        extensionNarrationSeconds: 0,
        totalNarrationSeconds: 240,
      },
      reviewState: "pending_review",
    },
    {
      id: "M2S2",
      order: 2,
      title: "敌后战场的开辟和敌后抗日根据地的创建",
      identity: "抗战史料研学员",
      timeLabel: "1937年11月—1938年4月；广西档案延伸至1945年初",
      mainVenue: "晋察冀边区",
      archiveSlices: [
        "山西山区、河北和山东平原",
        "华中新四军活动区域",
        "1938年11月—1941年1月·桂林八路军办事处",
        "1939年11月·钦廉、邕宁八尺区与中越边境",
        "1944年夏—9月·临桂—阳朔",
        "1945年初·桂东南与钦廉四属",
      ],
      taskIds: ["T06"],
      sequence: [
        "M2S2_N01",
        "M2S2_N02",
        "M2S2_N03",
        "M2S2_N04",
        "M2S2_N05",
        "GX05_LABEL",
        "M2S2_GX08",
        "M2S2_GX07",
        "T06",
      ],
      nodes: [
        {
          id: "M2S2_N01",
          sceneId: "M2S2",
          order: 10,
          nodeType: "textbook_claim",
          title: "太原失守后向敌后实施战略展开",
          timeAnchor: "1937年11月",
          estimatedSeconds: 56,
          claimId: "M2S2_CLAIM_ENEMY_REAR_STRATEGIC_DEPLOYMENT",
          claim:
            "1937年11月太原失守后，以中国共产党为主体的游击战争在华北上升到主要地位，党领导的军队着重向敌后实施战略展开。",
          bodyZh:
            "1937年11月太原失守后，以中国共产党为主体的游击战争在华北上升到主要地位。党领导的军队根据洛川会议的决定，着重向敌后实施战略展开，发动独立自主的敌后游击战争。当日军依仗优势兵力，气势汹汹地向前猛进时，国民党的军队节节后退，而装备简陋的八路军却分散地大踏步地向敌后挺进。他们同地方党组织相结合，组织工作团，建立战地动员委员会、抗日救国会等半政权性质的组织。",
          presentationType: "strategic_situation_map",
          assetSuggestions: [
            {
              key: "M2S2_ASSET_STRATEGIC_DEPLOYMENT",
              kind: "programmatic_2_5d",
              title: "华北敌后战略展开地图",
              usage: "将太原失守、敌后挺进和地方组织结合呈现为可逐层打开的关系。",
              provenanceGuard: "只呈现教材可支持的战略关系，不自行补齐具体行军线路。",
            },
          ],
          source: textbookSource("80—81", "100—101"),
          reviewState: "pending_review",
        },
        {
          id: "M2S2_N02",
          sceneId: "M2S2",
          order: 20,
          nodeType: "textbook_claim",
          title: "晋察冀边区临时行政委员会",
          timeAnchor: "1938年1月10日",
          estimatedSeconds: 45,
          claimId: "M2S2_CLAIM_FIRST_UNIFIED_FRONT_GOVERNMENT",
          claim:
            "1938年1月10日成立的晋察冀边区临时行政委员会，是敌后由中国共产党领导建立的第一个统一战线性质的抗日民主政权。",
          bodyZh:
            "1938年1月10日，晋察冀边区临时行政委员会成立。这是敌后由中国共产党领导建立的第一个统一战线性质的抗日民主政权。敌后抗日政权的建立，恢复了国民党政权溃逃后陷入一片混乱的社会秩序，使广大人民群众看到了光明和希望，迅速赢得了人民的拥护。",
          presentationType: "governance_timeline",
          assetSuggestions: [
            {
              key: "M2S2_ASSET_GOVERNMENT_TIMELINE",
              kind: "interface",
              title: "1938年1月10日政权建设档案卡",
              usage: "将日期、机构全称、性质与社会作用分为四个可核对字段。",
              provenanceGuard: "保留教材正文的“临时”字样，脚注延伸不得改写T06。",
            },
          ],
          source: textbookSource("81", "101"),
          reviewState: "pending_review",
        },
        {
          id: "M2S2_N03",
          sceneId: "M2S2",
          order: 30,
          nodeType: "textbook_claim",
          title: "向河北、山东平原挺进与新四军游击战",
          timeAnchor: "1938年4月",
          estimatedSeconds: 44,
          claimId: "M2S2_CLAIM_BASE_AREA_EXPANSION",
          claim:
            "1938年4月，党中央决定八路军三大主力分别向河北、山东平原挺进，新四军各部也利用复杂地形开展游击战。",
          bodyZh:
            "1938年4月，党中央作出决定：将原在山西山区的八路军三大主力分别向河北和山东的平原地区挺进，开辟新的抗日根据地。这对于习惯山地作战的八路军来说是一次新的挑战。新四军各部也利用山区和河湖港汊等复杂地形开展游击战。敌后抗日游击战争出现了新的局面。",
          presentationType: "terrain_expansion_map",
          assetSuggestions: [
            {
              key: "M2S2_ASSET_TERRAIN_EXPANSION",
              kind: "programmatic_3d",
              title: "山地-平原-河湖港汊地形比较沙盘",
              usage: "让学习者比较不同地形下开辟根据地的条件与挑战。",
              provenanceGuard: "地形为类型化教学示意，不标作具体战斗现场。",
              suggestedVisibleLabel: "场景化复原·非档案地形",
            },
          ],
          source: textbookSource("81", "101"),
          reviewState: "pending_review",
        },
        {
          id: "M2S2_N04",
          sceneId: "M2S2",
          order: 40,
          nodeType: "textbook_claim",
          title: "正面战场与敌后战场的战略配合",
          timeAnchor: "1938年战略防御向战略相持转化期",
          estimatedSeconds: 40,
          claimId: "M2S2_CLAIM_TWO_BATTLEFIELDS",
          claim: "敌后战场的开辟使中国抗日战争形成战略上互相配合的正面战场与敌后战场。",
          bodyZh:
            "敌后战场的开辟，使中国抗日战争形成战略上互相配合的两个战场，一个是主要由国民党军队担负的正面战场，一个是主要由共产党军队担负的敌后战场。敌后战场的迅速发展，牵制了大量日军。这是抗日战争由战略防御转到战略相持的一个重要条件。",
          presentationType: "dual_theater_map",
          assetSuggestions: [
            {
              key: "M2S2_ASSET_DUAL_THEATER",
              kind: "programmatic_2_5d",
              title: "两个战场战略配合图",
              usage: "用两层地图和一条战略相持转化证据链呈现互相配合关系。",
              provenanceGuard: "不将“互相配合”简化为相同指挥体系或同一作战现场。",
            },
          ],
          source: textbookSource("81", "101"),
          reviewState: "pending_review",
        },
        {
          id: "M2S2_N05",
          sceneId: "M2S2",
          order: 50,
          nodeType: "textbook_claim",
          title: "人民军队在敌后艰苦环境中不断发展壮大的奥秘",
          timeAnchor: "1937年11月起的敌后根据地创建",
          estimatedSeconds: 55,
          claimId: "M2S2_CLAIM_PEOPLE_SUPPORT",
          claim: "人民的支持和拥护，是人民军队能够在敌后艰苦环境中不断发展壮大的奥秘所在。",
          bodyZh:
            "中国共产党领导的军队在敌后开展的游击战争，是世界历史上罕见的艰苦战争。八路军将士面对日本侵略军的反复“扫荡”，只有极简陋的武器装备，没有来自后方的枪支弹药的接济。他们在敌人的包围中创建抗日根据地，那里大多是穷乡僻壤，物质条件极其恶劣。然而，经过艰苦卓绝的斗争，他们终于在敌后站稳了脚跟，打开了局面，其中的关键是得到人民的支持和拥护。这是人民军队能够在敌后艰苦环境中不断发展壮大的奥秘所在。",
          presentationType: "people_support_witness",
          assetSuggestions: [
            {
              key: "M2S2_ASSET_PEOPLE_SUPPORT",
              kind: "programmatic_2_5d",
              title: "艰苦条件与人民支持证据墙",
              usage: "将物质条件、根据地创建与人民支持三者组成因果链。",
              provenanceGuard: "只使用经审核的历史图像或明确标注复原性创作。",
            },
          ],
          source: textbookSource("81—82", "101—102"),
          reviewState: "pending_review",
        },
        {
          id: "M2S2_GX08",
          sceneId: "M2S2",
          order: 70,
          nodeType: "local_extension",
          title: "八路军桂林办事处",
          timeAnchor: "1938年11月—1941年1月",
          estimatedSeconds: 88,
          claimId: "M2S2_CLAIM_GX_GUILIN_OFFICE",
          claim:
            "八路军桂林办事处积极领导和推动了国统区的抗日民主运动，使桂林成为当时著名的抗战“文化城”",
          bodyZh: frozenText.gx05C08,
          presentationType: "organization_network",
          assetSuggestions: [
            {
              key: "M2S2_ASSET_GX_OFFICE_NETWORK",
              kind: "interface",
              title: "八路军桂林办事处四项职能网络",
              usage:
                "以统战、宣传、物资与人才输送、组织联络四张职能卡连接延安、前线和南方各省党组织。",
              provenanceGuard: "标明“拓展材料”；关系图不得将不同地点伪装为同一物理现场。",
            },
          ],
          source: localExtensionSource(
            ["SRC_GX_WAR_HISTORY", "SRC_GUILIN_CITY_HISTORY"],
            "广西政协文史资料及八办公办资料",
          ),
          reviewState: "pending_review",
          visibleLabel: "拓展材料",
        },
        {
          id: "M2S2_GX07",
          sceneId: "M2S2",
          order: 80,
          nodeType: "local_extension",
          title: "广西地方抗日武装斗争",
          timeAnchor: "1939年11月—1945年初",
          estimatedSeconds: 147,
          claimId: "M2S2_CLAIM_GX_ARMED_RESISTANCE_TIMELINE",
          claim: "广西各地党组织在严重困难中发动群众、组织抗日武装并开展敌后游击斗争。",
          bodyZh: frozenText.gx05C07,
          presentationType: "local_extension_timeline",
          assetSuggestions: [
            {
              key: "M2S2_ASSET_GX_RESISTANCE_TIMELINE",
              kind: "programmatic_2_5d",
              title: "1939—1945年广西地方武装斗争时间轴",
              usage: "严格按C07原段顺序分层承载1939年11月、1944年夏、1944年9月和1945年初四个节点。",
              provenanceGuard: "标明“拓展材料”；节点分层不得删文、改序或把离散时空合并为同一现场。",
            },
          ],
          source: localExtensionSource(
            ["SRC_GX_PARTY_HISTORY_2020"],
            "中共广西区委党史研究室《旗帜不倒 抗战到底》",
          ),
          reviewState: "pending_review",
          visibleLabel: "拓展材料",
        },
      ],
      extensionLabel: {
        id: "GX05_LABEL",
        label: "【广西融入⑤】",
        visibleLabel: "拓展材料",
        appliesTo: ["M2S2_GX08", "M2S2_GX07"],
      },
      timing: {
        mainNarrationSeconds: 240,
        extensionNarrationSeconds: 235,
        totalNarrationSeconds: 475,
      },
      reviewState: "pending_review",
    },
    {
      id: "M2S3",
      order: 3,
      title: "坚持统一战线中的独立自主",
      identity: "抗战史料研学员",
      timeLabel: "1937年11月底；1938年9—11月",
      mainVenue: "延安六届六中全会场景及相关文件档案空间",
      archiveSlices: [],
      taskIds: ["T07"],
      sequence: ["M2S3_N01", "M2S3_N02", "M2S3_N03", "M2S3_N04", "T07"],
      nodes: [
        {
          id: "M2S3_N01",
          sceneId: "M2S3",
          order: 10,
          nodeType: "textbook_claim",
          title: "统一战线中的独立自主原则",
          timeAnchor: "全民族抗战伊始",
          estimatedSeconds: 42,
          claimId: "M2S3_CLAIM_INDEPENDENCE_IN_UNITY",
          claim:
            "党如何正确处理统一战线中统一和独立、团结和斗争的关系，成为对抗战成败具有决定意义的问题。",
          bodyZh:
            "在全民族抗日战争中，由于复杂的国际国内形势和国共两党间存在着两条不同的抗战路线，党如何正确处理统一战线中的统一和独立、团结和斗争的关系，成为对抗战成败具有决定意义的问题。全民族抗战伊始，党中央就指出，必须坚持统一战线中的独立自主原则。",
          presentationType: "principle_balance",
          assetSuggestions: [
            {
              key: "M2S3_ASSET_PRINCIPLE_BALANCE",
              kind: "interface",
              title: "统一-独立与团结-斗争关系板",
              usage: "用两组关系轴说明原则的双重要求，避免把独立自主误读为脱离统一战线。",
              provenanceGuard: "概念关系仅按教材表述组织，不自行引申政策结论。",
            },
          ],
          source: textbookSource("82", "102"),
          reviewState: "pending_review",
        },
        {
          id: "M2S3_N02",
          sceneId: "M2S3",
          order: 20,
          nodeType: "textbook_claim",
          title: "王明错误观点及其受到的抵制",
          timeAnchor: "1937年11月底—1938年7月",
          estimatedSeconds: 50,
          claimId: "M2S3_CLAIM_WANG_MING_LINE_RESISTED",
          claim:
            "王明主张的“一切经过统一战线”“一切服从抗日”错误观点受到毛泽东等中央领导人的坚决抵制。",
          bodyZh:
            "1937年11月底，中共驻共产国际代表、共产国际执委王明从苏联回到延安。他主张“一切经过统一战线”“一切服从抗日”，把共产党和人民军队的活动限制在国民党允许的范围内，对洛川会议以来党在统一战线问题上的许多正确观点和政策提出批评。这些错误观点受到毛泽东等中央领导人的坚决抵制。1938年7月，共产国际领导人也明确表示，在中共中央内部应支持毛泽东的领导地位；王明缺乏实际工作经验，不应争当领袖。",
          presentationType: "document_comparison",
          assetSuggestions: [
            {
              key: "M2S3_ASSET_DOCUMENT_COMPARISON",
              kind: "interface",
              title: "统一战线两种观点文件对照墙",
              usage: "将错误口号、限制性后果和中央抵制三层并列，让学习者核对原则差异。",
              provenanceGuard: "不伪造原始文件扫描；共产国际表态只按教材用语呈现。",
            },
          ],
          source: textbookSource("82—83", "102—103"),
          reviewState: "pending_review",
        },
        {
          id: "M2S3_N03",
          sceneId: "M2S3",
          order: 30,
          nodeType: "textbook_claim",
          title: "六届六中全会提出马克思主义中国化命题",
          timeAnchor: "1938年9—11月",
          estimatedSeconds: 42,
          claimId: "M2S3_CLAIM_MARXISM_SINICIZATION_PROPOSITION",
          claim: "党的扩大的六届六中全会首次提出马克思主义中国化的命题。",
          bodyZh:
            "1938年9月至11月，党的扩大的六届六中全会在延安举行。全会首次提出马克思主义中国化的命题。毛泽东明确指出：“马克思主义在中国具体化，使之在其每一表现中带着必须有的中国的特性，即是说，按照中国的特点去应用它，成为全党亟待了解并亟须解决的问题。”",
          presentationType: "conference_document_wall",
          assetSuggestions: [
            {
              key: "M2S3_ASSET_SIXTH_PLENUM_DOCUMENT",
              kind: "interface",
              title: "六届六中全会命题与引文文献墙",
              usage: "将会议时间、命题和毛泽东教材载述引文组成可逐项核对的证据链。",
              provenanceGuard: "引文只按教材载录呈现，版式标明教学研读示意而非原始档案。",
            },
          ],
          source: textbookSource("83", "103"),
          reviewState: "pending_review",
        },
        {
          id: "M2S3_N04",
          sceneId: "M2S3",
          order: 40,
          nodeType: "textbook_claim",
          title: "党的六届六中全会的内容与意义",
          timeAnchor: "1938年9—11月",
          estimatedSeconds: 46,
          claimId: "M2S3_CLAIM_SIXTH_PLENUM_SIGNIFICANCE",
          claim:
            "六届六中全会确定统一战线中独立自主的方针和敌后抗战战略部署，进一步巩固毛泽东在全党的领导地位。",
          bodyZh:
            "全会强调“我们的方针是统一战线中的独立自主，既统一，又独立”，确定敌后抗战总的战略部署是“巩固华北，发展华中”。全会重申党的纪律，即个人服从组织，少数服从多数，全党服从中央。党的扩大的六届六中全会是一次具有重大历史意义的会议。这次全会正确地分析了抗日战争的形势，规定了党在抗战新阶段的任务，为实现党对抗日战争的领导进行了全面的战略规划，基本上纠正了王明的右倾错误，进一步巩固了毛泽东在全党的领导地位，统一了全党的思想和步调，推动了各项工作的迅速发展。",
          presentationType: "strategy_board",
          assetSuggestions: [
            {
              key: "M2S3_ASSET_SIXTH_PLENUM_STRATEGY",
              kind: "interface",
              title: "方针-部署-纪律-意义四层战略板",
              usage: "引导学习者把会议命题、具体方针和历史意义分层归纳。",
              provenanceGuard: "保持教材的层次与评价性表述，不将T07待审题干反向注入教材主张。",
            },
          ],
          source: textbookSource("83", "103"),
          reviewState: "pending_review",
        },
      ],
      timing: {
        mainNarrationSeconds: 180,
        extensionNarrationSeconds: 0,
        totalNarrationSeconds: 180,
      },
      reviewState: "pending_review",
    },
  ],
  timing: {
    mainNarrationSeconds: 660,
    extensionNarrationSeconds: 235,
    totalNarrationSeconds: 895,
  },
  reviewState: "pending_review",
  reviewNotes: [
    {
      id: "M2_REVIEW_PAGE_REFERENCE",
      scope: "M2全部教材节点",
      priority: "before_frontend_release",
      state: "open",
      note: "原始DeepSeek方案将源PDF第98—103页误写为“书中页码”；本蓝图依逐页渲染核对结果登记书内印刷第78—83页 / 源PDF第98—103页双页码。",
    },
  ],
} satisfies M2Blueprint;
